import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurfaceMutedHover,
  base_colorSurfaceSecondary,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_lg,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { paddingHorizontal } from '../billPayFlyoutChrome.style'

/* Same scroll/pad rhythm as the Vendor Details panel and the shell's fields
   column, so switching tabs never shifts the layout. */
export const tabBody = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
  padding: ${spacing(6)} ${paddingHorizontal};
`

/* The ACH account summary card. Framed by a hairline — the Bill Pay flyouts
   group with borders rather than grey cards (../billPayFlyoutChrome.style).
   The fill is one step off the flyout's white so the card reads as a panel:
   on the raised-surface token it was the same white as the body behind it and
   only the border separated them, where the prototype tints it (measured
   #FCFCFF on white, 2026-08-27). */
export const card = css`
  display: flex;
  flex-direction: column;
  background: ${base_colorSurfaceSecondary};
  border: 1px solid ${base_colorContentBorder};
  /* Prototype geometry, measured 2026-08-27: 10px corners and an even 24px
     inset, against the 8px / 16×20 this carried. */
  border-radius: 10px;
  padding: ${spacing(6)};
`

export const cardHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(2)};
`

export const cardTitle = css`
  margin: 0;
  ${font_lg}
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
`

export const rows = css`
  display: flex;
  flex-direction: column;
  margin-top: ${spacing(2)};
`

/* Label-left / value-right, with a hairline divider between rows but not above
   the first (the prototype's row anatomy). */
export const row = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
  padding: ${spacing(3)} 0;

  & + & {
    border-top: 1px solid ${base_colorContentBorder};
  }
`

export const rowLabel = css`
  ${font_sm}
  color: ${text_colorTextSecondary};
`

export const rowValue = css`
  ${font_md}
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
  text-align: right;
  word-break: break-word;
`

/* Kebab trigger — an icon-only button. Reset the native chrome, keep a visible
   focus ring (never remove focus styles without a replacement). */
export const kebabTrigger = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing(1)};
  border: none;
  border-radius: ${spacing(1)};
  background: transparent;
  cursor: pointer;
  color: ${text_colorTextSecondary};

  &:hover {
    background: ${base_colorSurfaceMutedHover};
  }

  &:focus-visible {
    outline: 2px solid ${text_colorTextPrimary};
    outline-offset: 2px;
  }
`

export const menuWrap = css`
  position: relative;
`

export const menu = css`
  position: absolute;
  top: calc(100% + ${spacing(1)});
  right: 0;
  z-index: 10;
  /* Sized to its longest item rather than a fixed width: at 180px "Remove
     account" wrapped onto a second line, and a fixed bump would only move the
     problem to whichever translation is longer (fr-CA's is). Anchored right, so
     the extra width grows leftward and stays over the card. */
  width: max-content;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  padding: ${spacing(1)} 0;
  background: ${base_colorRaisedSurface};
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${spacing(2)};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`

export const menuItem = css`
  ${font_md}
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${spacing(2)} ${spacing(4)};
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${base_colorSurfaceMutedHover};
  }

  &:focus-visible {
    outline: 2px solid ${text_colorTextPrimary};
    outline-offset: -2px;
  }
`

/* Shared with the header's "Archive Vendor" item so the two destructive menus
   in this flyout read as the same control. Composed after `menuItem` at the call
   site, so its font and padding win. */
export {
  severeMenuItem as menuItemSevere,
  severeMenuItemIcon as menuItemIcon,
} from './VendorFlyout.style'

/* State B intro line above the embed. */
export const sectionNote = css`
  margin: 0;
  ${font_md}
  color: ${text_colorTextSecondary};
`
